/**
 * Por que uma IA não leu: a foto não serve, ou o serviço não respondeu (03/11).
 *
 * A revisão da homologação (Gisele, 03/11) tinha três queixas que pareciam três defeitos — a
 * placa "não lida" em fotos legíveis, os eixos sem contagem, a imagem sem aparecer — e a tela
 * não dizia nada que as separasse. "Placa não lida" é o que a tela escreve quando o modelo
 * **olhou e não leu**; quando o serviço de placa nem respondeu (tempo esgotado, contêiner fora),
 * o texto era o mesmo, e o operador conferia à mão uma placa que ninguém tinha tentado ler.
 *
 * Os fatos já existem: o módulo de Análise guarda o desfecho de cada capacidade em cada imagem
 * (`/analise/imagens/{id}/resultados`), e `INDISPONIVEL` quer dizer falha **nossa**. Aqui eles
 * viram frase — por IA, quantas vistas, e o motivo que o serviço deu.
 */
import type { ImagemDaAnalise, ResultadoDaCapacidade } from "../api/tipos";

/** O nome de cada IA na tela, na ordem em que aparecem. */
const NOME_DA_IA: Readonly<Record<string, string>> = {
  ocr_placa: "Placa",
  contagem_eixos: "Eixos",
  classificacao_veiculo: "Classe",
  anonimizacao: "Anonimização",
};

/**
 * - `servico`: o serviço não respondeu (`INDISPONIVEL`) — falha nossa, e não da foto;
 * - `imagem`: o serviço respondeu que a imagem não serve (`ENTRADA_INADEQUADA`);
 * - `garantia`: a anonimização rodou e **não garantiu** que a cena está anônima.
 */
export type CausaDaFalha = "servico" | "imagem" | "garantia";

export interface ProblemaDeIa {
  capacidade: string;
  nome: string;
  causa: CausaDaFalha;
  /** Em quantas vistas aconteceu. */
  vistas: number;
  /** De quantas vistas se sabe o resultado. */
  de: number;
  /** O motivo que o serviço deu, curto — a primeira ocorrência. */
  motivo: string | null;
}

const MAXIMO_DO_MOTIVO = 140;

function curto(texto: string | null | undefined): string | null {
  const limpo = (texto ?? "").replace(/\s+/g, " ").trim();
  if (!limpo) return null;
  return limpo.length > MAXIMO_DO_MOTIVO
    ? `${limpo.slice(0, MAXIMO_DO_MOTIVO - 1).trimEnd()}…`
    : limpo;
}

function causaDe(resultado: ResultadoDaCapacidade): {
  causa: CausaDaFalha;
  motivo: string | null;
} | null {
  if (resultado.desfecho === "INDISPONIVEL")
    return { causa: "servico", motivo: curto(resultado.erro) };
  if (resultado.desfecho === "ENTRADA_INADEQUADA")
    return { causa: "imagem", motivo: curto(resultado.erro) };
  if (
    resultado.capacidade === "anonimizacao" &&
    resultado.desfecho === "SUCESSO" &&
    resultado.valor?.completa === false
  )
    return {
      causa: "garantia",
      motivo: curto(String(resultado.valor.observacao ?? "")),
    };
  return null;
}

/**
 * O que deu errado, por IA, nas vistas de um trânsito. Vazio quando tudo correu bem — e
 * também quando ninguém sabe (os resultados não vieram: Análise sem licença, imagem expurgada),
 * porque dizer "falhou" sem o dado seria inventar o diagnóstico.
 *
 * Mais de uma causa na mesma IA (uma vista sem resposta, outra com a imagem recusada) vira um
 * problema por causa; o do serviço vem primeiro, que é o que a pessoa pode resolver.
 */
export function problemasDasIas(
  vistas: readonly Pick<ImagemDaAnalise, "imagem_id">[],
  porVista: Readonly<Record<string, readonly ResultadoDaCapacidade[]>>,
): ProblemaDeIa[] {
  const sabidas = vistas.filter(
    (v) => (porVista[v.imagem_id]?.length ?? 0) > 0,
  );
  if (sabidas.length === 0) return [];
  const problemas: ProblemaDeIa[] = [];
  for (const [capacidade, nome] of Object.entries(NOME_DA_IA)) {
    const porCausa = new Map<
      CausaDaFalha,
      { vistas: number; motivo: string | null }
    >();
    for (const vista of sabidas) {
      const resultado = porVista[vista.imagem_id]?.find(
        (r) => r.capacidade === capacidade,
      );
      const falha = resultado ? causaDe(resultado) : null;
      if (!falha) continue;
      const atual = porCausa.get(falha.causa);
      porCausa.set(falha.causa, {
        vistas: (atual?.vistas ?? 0) + 1,
        motivo: atual?.motivo ?? falha.motivo,
      });
    }
    for (const causa of ["servico", "imagem", "garantia"] as const) {
      const achada = porCausa.get(causa);
      if (achada)
        problemas.push({
          capacidade,
          nome,
          causa,
          vistas: achada.vistas,
          de: sabidas.length,
          motivo: achada.motivo,
        });
    }
  }
  return problemas;
}

const TEXTO_DA_CAUSA: Readonly<Record<CausaDaFalha, string>> = {
  servico: "o serviço não respondeu",
  imagem: "a imagem não serve para esta IA",
  garantia: "sem garantia de anonimização",
};

/** "Placa: o serviço não respondeu em 3 de 3 vistas — tempo esgotado." */
export function fraseDoProblema(p: ProblemaDeIa): string {
  const onde = `${p.vistas} de ${p.de} vista${p.de > 1 ? "s" : ""}`;
  return `${p.nome}: ${TEXTO_DA_CAUSA[p.causa]} em ${onde}${p.motivo ? ` — ${p.motivo}` : ""}`;
}

/** O problema de uma IA que é do **serviço**, ou `undefined`. */
export function falhaDoServico(
  problemas: readonly ProblemaDeIa[],
  capacidade: string,
): ProblemaDeIa | undefined {
  return problemas.find(
    (p) => p.capacidade === capacidade && p.causa === "servico",
  );
}

/**
 * Por que **esta** imagem ainda não tem a cópia anônima, para a moldura do visor — ou `null`
 * quando não há o que dizer além de "ainda não passou". O original continua sem aparecer (a
 * decisão de 21/09 não mudou): o que muda é que a moldura deixa de parecer um defeito da foto.
 */
export function motivoDaFaltaDeAnonimizacao(
  resultados: readonly ResultadoDaCapacidade[] | undefined,
): string | null {
  const r = resultados?.find((x) => x.capacidade === "anonimizacao");
  if (!r) return null;
  const falha = causaDe(r);
  if (!falha) return null;
  // Sem o ponto final do serviço: a frase continua depois dele.
  const motivo = falha.motivo?.replace(/[.\s]+$/, "") ?? null;
  if (falha.causa === "servico")
    return "o serviço de anonimização não respondeu — é refeita sozinha e a foto aparece em seguida; o original não é exibido";
  if (falha.causa === "garantia")
    return `sem garantia de anonimização${motivo ? ` — ${motivo}` : ""}; o original não é exibido`;
  return `a anonimização recusou a imagem${motivo ? ` — ${motivo}` : ""}; o original não é exibido`;
}
